import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
    const { t } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [formData, setFormData] = useState({
        nom: '',
        raison_sociale: '',
        siret: '',
        sujet: '',
        message: '',
        email: '',
        telephone: ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubjectChange = (value) => {
        setFormData(prev => ({ ...prev, sujet: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await axios.post(`${API}/contacts`, formData);
            setSuccess(true);
            toast.success(t('Message envoyé avec succès !', 'Message sent successfully!'));
            setFormData({
                nom: '',
                raison_sociale: '',
                siret: '',
                sujet: '',
                message: '',
                email: '',
                telephone: ''
            });
        } catch (error) {
            toast.error(t('Erreur lors de l\'envoi du message.', 'Error sending message.'));
        } finally {
            setLoading(false);
        }
    };

    const subjects = [
        { value: 'service', label: t('Demande de service', 'Service request') },
        { value: 'devis', label: t('Demande de devis', 'Quote request') },
        { value: 'collaboration', label: t('Proposition de collaboration', 'Collaboration proposal') },
        { value: 'autre', label: t('Autre', 'Other') }
    ];

    return (
        <main data-testid="contact-page" className="min-h-screen pt-24">
            {/* Header */}
            <section className="py-16 md:py-24">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
                    <div className="max-w-3xl">
                        <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-white mb-6">
                            Contact
                        </h1>
                        <p className="text-lg text-slate-400">
                            {t(
                                'Une question, un projet, une collaboration ? N\'hésitez pas à me contacter.',
                                'A question, a project, a collaboration? Feel free to contact me.'
                            )}
                        </p>
                    </div>
                </div>
            </section>

            {/* Contact Form & Info */}
            <section className="pb-24">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
                    <div className="grid lg:grid-cols-3 gap-12">
                        {/* Contact Info */}
                        <div className="lg:col-span-1 space-y-6">
                            <div className="glass-card p-6">
                                <h3 className="text-lg font-bold text-white mb-4">DKR CONSULTING</h3>
                                <ul className="space-y-4">
                                    <li className="flex items-start gap-3">
                                        <MapPin className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                                        <div>
                                            <p className="text-white font-medium">{t('France / Sénégal / International', 'France / Senegal / International')}</p>
                                        </div>
                                    </li>
                                    <li className="flex items-center gap-3">
                                        <Mail className="w-5 h-5 text-blue-400 flex-shrink-0" />
                                        <a href="mailto:contact@dkr-consulting.com" className="text-white font-medium hover:text-blue-400 transition-colors">
                                            contact@dkr-consulting.com
                                        </a>
                                    </li>
                                </ul>
                            </div>

                            <div className="glass-card p-6">
                                <h3 className="text-lg font-bold text-white mb-4">Djiby NDIAYE</h3>
                                <p className="text-white font-medium text-sm mb-2">
                                    {t('Ingénieur Informatique', 'IT Engineer')}
                                </p>
                                <p className="text-white font-medium text-sm">
                                    Business Analyst Senior
                                </p>
                            </div>
                        </div>

                        {/* Form */}
                        <div className="lg:col-span-2">
                            {success ? (
                                <div className="glass-card p-12 text-center" data-testid="contact-success">
                                    <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-6" />
                                    <h3 className="text-2xl font-semibold text-white mb-4">
                                        {t('Message envoyé !', 'Message sent!')}
                                    </h3>
                                    <p className="text-slate-400 mb-6">
                                        {t(
                                            'Merci pour votre message. Je vous répondrai dans les plus brefs délais.',
                                            'Thank you for your message. I will respond as soon as possible.'
                                        )}
                                    </p>
                                    <Button 
                                        onClick={() => setSuccess(false)}
                                        variant="outline"
                                        className="rounded-full"
                                    >
                                        {t('Envoyer un autre message', 'Send another message')}
                                    </Button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="glass-card p-8" data-testid="contact-form">
                                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                                        <div>
                                            <Label htmlFor="nom" className="text-white font-bold mb-2 block">
                                                {t('Nom / Prénom', 'Name')} *
                                            </Label>
                                            <Input
                                                id="nom"
                                                name="nom"
                                                value={formData.nom}
                                                onChange={handleChange}
                                                required
                                                className="bg-slate-700 border-slate-500 text-white font-medium placeholder:text-slate-400"
                                                placeholder={t('Votre nom complet', 'Your full name')}
                                                data-testid="input-nom"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="raison_sociale" className="text-white font-bold mb-2 block">
                                                {t('Raison sociale', 'Company name')}
                                            </Label>
                                            <Input
                                                id="raison_sociale"
                                                name="raison_sociale"
                                                value={formData.raison_sociale}
                                                onChange={handleChange}
                                                className="bg-slate-700 border-slate-500 text-white font-medium placeholder:text-slate-400"
                                                placeholder={t('Nom de votre entreprise', 'Your company name')}
                                                data-testid="input-raison-sociale"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                                        <div>
                                            <Label htmlFor="email" className="text-white font-bold mb-2 block">
                                                Email *
                                            </Label>
                                            <Input
                                                id="email"
                                                name="email"
                                                type="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                                className="bg-slate-700 border-slate-500 text-white font-medium placeholder:text-slate-400"
                                                placeholder="votre@email.com"
                                                data-testid="input-email"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="telephone" className="text-white font-bold mb-2 block">
                                                {t('Téléphone', 'Phone')}
                                            </Label>
                                            <Input
                                                id="telephone"
                                                name="telephone"
                                                type="tel"
                                                value={formData.telephone}
                                                onChange={handleChange}
                                                className="bg-slate-700 border-slate-500 text-white font-medium placeholder:text-slate-400"
                                                placeholder="+33 6 00 00 00 00"
                                                data-testid="input-telephone"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                                        <div>
                                            <Label htmlFor="siret" className="text-white font-bold mb-2 block">
                                                SIRET / NINEA
                                            </Label>
                                            <Input
                                                id="siret"
                                                name="siret"
                                                value={formData.siret}
                                                onChange={handleChange}
                                                className="bg-slate-700 border-slate-500 text-white font-medium placeholder:text-slate-400"
                                                placeholder={t('Optionnel', 'Optional')}
                                                data-testid="input-siret"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="sujet" className="text-white font-bold mb-2 block">
                                                {t('Sujet', 'Subject')} *
                                            </Label>
                                            <Select value={formData.sujet} onValueChange={handleSubjectChange} required>
                                                <SelectTrigger 
                                                    className="bg-slate-700 border-slate-500 text-white font-medium"
                                                    data-testid="select-sujet"
                                                >
                                                    <SelectValue placeholder={t('Sélectionner un sujet', 'Select a subject')} />
                                                </SelectTrigger>
                                                <SelectContent className="bg-slate-700 border-slate-500">
                                                    {subjects.map(s => (
                                                        <SelectItem key={s.value} value={s.value} className="text-white font-medium hover:bg-slate-600">
                                                            {s.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>

                                    <div className="mb-6">
                                        <Label htmlFor="message" className="text-white font-bold mb-2 block">
                                            Message *
                                        </Label>
                                        <Textarea
                                            id="message"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            required
                                            rows={6}
                                            className="bg-slate-700 border-slate-500 text-white font-medium placeholder:text-slate-400 resize-none"
                                            placeholder={t('Décrivez votre projet ou votre demande...', 'Describe your project or request...')}
                                            data-testid="input-message"
                                        />
                                    </div>

                                    <Button 
                                        type="submit"
                                        size="lg"
                                        disabled={loading}
                                        className="btn-glow bg-blue-600 hover:bg-blue-500 text-white rounded-full px-8 w-full md:w-auto"
                                        data-testid="submit-contact"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                                                {t('Envoi en cours...', 'Sending...')}
                                            </>
                                        ) : (
                                            <>
                                                <Send className="mr-2 w-5 h-5" />
                                                {t('Envoyer le message', 'Send message')}
                                            </>
                                        )}
                                    </Button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};
